import { LegacyApiError, legacyApiRequest, ValidationError } from "./legacy-api-request";
import type { LegacyLoginResponse, LegacyLoginErrorResponse } from "./legacy-types";

/**
 * Login API
 * Handles authentication with the legacy Django API
 */
export const legacyLoginApi = {
  /**
   * Authenticate with email/password.
   *
   * @param username - User's email address
   * @param password - User's password
   * @returns Login response containing user_id and token
   * @throws {ValidationError} - When credentials are invalid (400 response)
   * @throws {NetworkError} - When network request fails
   * @throws {LegacyApiError} - For other API errors
   */
  async authenticate(username: string, password: string): Promise<LegacyLoginResponse> {
    try {
      const response = await legacyApiRequest<LegacyLoginResponse>({
        method: "POST",
        path: "/login/",
        body: { username, password },
        logMessage: "Authenticating user:",
      });

      if (!response.user_id || !response.token) {
        throw new LegacyApiError(200, "OK", "Invalid response format: missing user_id or token");
      }

      return response;
    } catch (error) {
      if (error instanceof LegacyApiError) {
        // Parse error response
        let errorData: LegacyLoginErrorResponse = {};
        try {
          errorData = JSON.parse(error.body ?? "{}");
        } catch {
          // Ignore JSON parse errors
        }

        if (error.status === 400) {
          throw new ValidationError(errorData.detail || "Invalid username or password", "credentials");
        }

        // Generic API error
        throw new LegacyApiError(error.status, error.statusText, errorData.detail ?? "Login failed");
      }

      // Re-throw other errors (NetworkError, etc.)
      throw error;
    }
  },
};
